import {
  computed,
  inject,
  provide,
  ref,
  type ComputedRef,
  type InjectionKey,
  type Ref,
} from 'vue';
import { useRenameItemMutation } from '../../data/useRenameItemMutation';
import { computeRenameMode } from '../../permissions/modes';
import type { FileManagerPermissionsState } from '../../permissions/useFileManagerPermissions';
import type { FileSystemItem } from '../../types';
import { splitName, withExtension } from './itemName';
import type { FileManagerDetailsState } from './useFileManagerDetails';
import type { FileManagerNavigationState } from './useFileManagerNavigation';

// splitName used to live here as a deliberate second copy of DetailsTableView.vue's own private one
// (RENAME_ITEM_PLAN.md's "Base/extension split logic is duplicated, not extracted"). That decision
// named its own trigger — a third call site — and useFileManagerCopy is it, so the function now lives
// in itemName.ts and all three import it. The two helpers below stay here: they are about renaming
// specifically, not about names in general.

// Pure: the modal's pre-filled input value. A directory has no extension concept, so its full
// name passes straight through; a file's extension is fixed/non-editable, so only its base name is
// offered for editing.
export const initialRenameValue = (item: FileSystemItem): string =>
  item.type === 'file' ? splitName(item.name).base : item.name;

// Pure: reassembles the full name to send to the mutation from the modal's (base-name-only)
// submission. A directory's newBaseName IS its full new name. A file's original extension (never
// user-edited) is re-appended, when it had one.
export const buildRenamedName = (item: FileSystemItem, newBaseName: string): string => {
  if (item.type !== 'file') return newBaseName;
  return withExtension(newBaseName, splitName(item.name).ext);
};

export type FileManagerRenameState = {
  isModalOpen: Ref<boolean>;
  pendingItem: Ref<FileSystemItem | null>;
  // The modal's pre-filled input value for whatever is currently pendingItem — derived here (via
  // initialRenameValue above) so RenameModal.vue stays a plain, self-contained prop/emit component
  // with no logic of its own, the same division of labor useFileManagerDelete draws around
  // describeItems/deleteMenuLabel.
  initialName: ComputedRef<string>;
  /** Path-scoped for the same reason as useFileManagerDelete's canDeleteAt — see its doc comment. */
  canRenameAt: (path: string) => boolean;
  // Drives the dimmed in-flight state (DetailsTableView's `.is-renaming`), same convention as
  // useFileManagerDelete's deletingPaths. A Set rather than a single nullable ref even though
  // rename only ever targets one item at a time — keeps the shape (and DetailsTableView's
  // class-binding code) identical to deletingPaths/transferringPaths, and covers a second rename
  // firing before the first settles without needing its own reasoning.
  renamingPaths: Ref<Set<string>>;
  requestRename: (item: FileSystemItem) => void;
  submitRename: (newBaseName: string) => void;
  cancelRename: () => void;
};

export const fileManagerRenameKey: InjectionKey<FileManagerRenameState> =
  Symbol('fileManagerRename');

// `navigation`/`permissions`/`details` are all taken as explicit params rather than self-injected
// via useFileManagerNavigation()/useFileManagerPermissions()/useFileManagerDetails(): this factory
// is called once, directly in FileManager.vue's own setup() — the same instance that provides all
// three — and inject() resolves against the *parent* instance's provides, so a component can never
// inject its own provide() call (see provideFileManagerDelete's identical reasoning).
export const provideFileManagerRename = (
  navigation: Pick<FileManagerNavigationState, 'currentPath' | 'navigateTo'>,
  permissions: Pick<FileManagerPermissionsState, 'permissionsAt'>,
  details: Pick<FileManagerDetailsState, 'detailsItem'>,
) => {
  const mutation = useRenameItemMutation(navigation);
  const { detailsItem } = details;

  const isModalOpen = ref(false);
  const pendingItem = ref<FileSystemItem | null>(null);
  const renamingPaths = ref<Set<string>>(new Set());

  const canRenameAt = (path: string) =>
    computeRenameMode(permissions.permissionsAt(path)) === 'enabled';

  const initialName = computed(() =>
    pendingItem.value ? initialRenameValue(pendingItem.value) : '',
  );

  // Hide-on-missing is enforced up the call chain (the context-menu builder omits the Rename entry
  // entirely when the target is not renamable), but this no-op guard is cheap insurance against any stale
  // entry point still calling through after a permission change — same convention as
  // useFileManagerDelete's requestDelete.
  const requestRename = (item: FileSystemItem) => {
    if (!canRenameAt(item.path)) return;
    pendingItem.value = item;
    isModalOpen.value = true;
  };

  const cancelRename = () => {
    isModalOpen.value = false;
  };

  const submitRename = (newBaseName: string) => {
    const item = pendingItem.value;
    // Closes immediately (optimistic), same convention as NewFolderModal/createFolder — the modal
    // itself never stays open awaiting the round-trip; a failure is surfaced later as an error
    // toast rather than by re-opening the form.
    isModalOpen.value = false;
    if (!item) return;

    const newName = buildRenamedName(item, newBaseName);
    renamingPaths.value = new Set(renamingPaths.value).add(item.path);

    // Cleared as soon as the mutation itself resolves — same convention as
    // useFileManagerDelete's deletingPaths (not after the invalidated query's refetch lands),
    // accepting the same brief "undimmed old name" flash Delete already has rather than
    // introducing a new await-refetch pattern.
    const removeFromRenaming = () => {
      const next = new Set(renamingPaths.value);
      next.delete(item.path);
      renamingPaths.value = next;
    };

    mutation.mutate(
      { item, newName },
      {
        onSuccess: (renamedItem) => {
          removeFromRenaming();
          // Details-panel sync: update in place, don't close (diverges from Delete's
          // close-the-panel precedent — a rename is cosmetic, the item still exists). Matches by
          // path OR id since both address the same still-open item.
          if (
            detailsItem.value &&
            (detailsItem.value.path === item.path || detailsItem.value.id === item.id)
          ) {
            detailsItem.value = renamedItem;
          }
        },
        onError: removeFromRenaming,
      },
    );
  };

  const state: FileManagerRenameState = {
    isModalOpen,
    pendingItem,
    initialName,
    canRenameAt,
    renamingPaths,
    requestRename,
    submitRename,
    cancelRename,
  };
  provide(fileManagerRenameKey, state);
  return state;
};

export const useFileManagerRename = () => {
  const state = inject(fileManagerRenameKey);
  if (!state) {
    throw new Error(
      'useFileManagerRename() must be called within a component tree provided by provideFileManagerRename()',
    );
  }
  return state;
};
